import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

// ============================================================
// VehicleInvolved — entidade retornada pelo backend
// ============================================================

export interface VehicleInvolved {
  vehicleId?: number;
  plateNumber?: string;
  municipality?: string;
  state?: string;
  vin?: string;
  color?: string;
  brandModel?: string;
  type?: string;
  species?: string;
  category?: string;
  yearOfManufacture?: number;
  yearOfModel?: number;
  lastLicensingYear?: number;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================
// VehicleInvolvedRequestDTO — enviado no POST/PUT
// ============================================================

export interface VehicleInvolvedRequestDTO {
  plateNumber?: string;
  municipality?: string;
  state?: string;
  vin?: string;
  color?: string;
  brandModel?: string;
  type?: string;
  species?: string;
  category?: string;
  yearOfManufacture?: number;
  yearOfModel?: number;
  lastLicensingYear?: number;
  /** Opcional: IDs dos proprietários já cadastrados */
  ownerIds?: number[];
}

// ============================================================
// Serviço
// ============================================================

@Injectable({
  providedIn: 'root'
})
export class VehicleInvolvedService {

  private readonly baseUrl = `${environment.apiUrl}/nucon/api/v1/vehicle`;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {}

  /** GET /{id} — Busca veículo por ID */
  getById(id: number): Observable<VehicleInvolved> {
    return this.http.get<VehicleInvolved>(`${this.baseUrl}/${id}`, this.httpOptions);
  }

  /** GET /all — Lista todos os veículos */
  getAll(): Observable<VehicleInvolved[]> {
    return this.http.get<VehicleInvolved[]>(`${this.baseUrl}/all`, this.httpOptions);
  }

  /** GET /placa/{plate} — Busca veículos pela placa (retorna lista) */
  getByPlate(plate: string): Observable<VehicleInvolved[]> {
    return this.http.get<VehicleInvolved[]>(
      `${this.baseUrl}/placa/${encodeURIComponent(plate)}`,
      this.httpOptions
    );
  }

  /** POST — Cria um novo veículo */
  create(dto: VehicleInvolvedRequestDTO): Observable<{ message: string; vehicleId: number }> {
    return this.http.post(
      this.baseUrl, dto,
      { ...this.httpOptions, responseType: 'text' as 'json' }
    ).pipe(
      map((raw: any) => this.safeParse(raw))
    );
  }

  /** PUT /{id} — Atualiza um veículo existente */
  update(id: number, dto: VehicleInvolvedRequestDTO): Observable<{ message: string; vehicleId: number }> {
    return this.http.put(
      `${this.baseUrl}/${id}`, dto,
      { ...this.httpOptions, responseType: 'text' as 'json' }
    ).pipe(
      map((raw: any) => this.safeParse(raw))
    );
  }

  /**
   * Tenta parse seguro de JSON, removendo Hibernate proxies quebrados.
   * O backend com @JsonIgnoreProperties pode falhar em listas lazy.
   */
  private safeParse(raw: any): { message: string; vehicleId: number } {
    if (typeof raw === 'object') return raw;
    try {
      // Remove segmentos como ":]]]..." que são Hibernate proxy serializado
      const sanitized = (raw as string).replace(/,?"\w+":\[?\]*]+/g, '');
      return JSON.parse(sanitized);
    } catch {
      return { message: '', vehicleId: 0 };
    }
  }

  /** DELETE /{id} — Exclui um veículo */
  delete(id: number): Observable<{ message: string; id: number }> {
    return this.http.delete<{ message: string; id: number }>(
      `${this.baseUrl}/${id}`, this.httpOptions
    );
  }
}
